import React, { useMemo } from "react";
import Chart from "react-apexcharts";
import { colors } from "../../../global";

export default function PieChart({
  data = [],
  labelKey = "Mode",
  valueKey = "value",
  title = "Incumbent Spend by Network",
}) {
  const fallbackKeys = useMemo(
    () => [labelKey, "Mode", "To State", "To_State", "ToState"],
    [labelKey]
  );

  const getLabel = (d) => {
    for (const k of fallbackKeys) {
      if (d && Object.prototype.hasOwnProperty.call(d, k)) return d[k];
    }
    return "";
  };

  // 1) Normalize + filter out junk rows (incl. "Grand Total")
  const rows = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data
      .map((d) => ({
        label: getLabel(d),
        value: Number(d?.[valueKey] ?? 0),
      }))
      .filter(
        (r) =>
          r.label &&
          r.label !== "Grand Total" &&
          Number.isFinite(r.value) &&
          r.value >= 0
      );
  }, [data, valueKey, getLabel]);

  const labels = rows.map((r) => r.label);
  const series = rows.map((r) => r.value);

  // 2) Cycle colors so charts with >5 slices still render nicely
  const basePalette = [
    colors?.accent,
    colors?.primary,
    colors?.success,
    colors?.info,
    colors?.warning,
  ].filter(Boolean);

  const colorScale =
    rows.length <= basePalette.length
      ? basePalette
      : rows.map((_, i) => basePalette[i % basePalette.length]);

  const options = {
    chart: {
      type: "pie",
      toolbar: { show: false },
      fontFamily:
        "ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial",
    },
    labels,
    colors: colorScale,
    legend: {
      position: "bottom",
      labels: { colors: "#374151" },
    },
    title: {
      text: title,
      align: "center",
      style: { fontWeight: 600, color: "#1f2937" },
    },
    dataLabels: {
      // ApexCharts passes percentage here for pie, so this is fine
      formatter: (val) => `${(val ?? 0).toFixed(1)}%`,
    },
    tooltip: {
      y: {
        formatter: (val) => {
          const percentage = val.toLocaleString(undefined, {
            style: "percent",
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          });

          return `${val} (${percentage})`;
        },
      },
    },

    plotOptions: { pie: { dataLabels: { offset: -10 } } },
  };

  if (!series.length || series.every((v) => !v)) {
    return (
      <div className="rounded-2xl border bg-white p-8 text-center text-gray-500 shadow-sm">
        No data to display.
      </div>
    );
  }

  return <Chart options={options} series={series} type="pie" height={360} />;
}
